// Import necessary libraries
import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import {
    API_ERROR_MESSAGE,
    FUNGUS_APP_NAME,
    LICHEN_APP_NAME,
    SELECTED_TIME_SPAN_FUNGUS,
    SELECTED_TIME_SPAN_LICHEN
} from '../data/constants'
import createPersistedState from 'vuex-persistedstate'
import {configUrl} from '../data/app-data'

const vuexSessionStorage = createPersistedState({
    storage: window.sessionStorage,
    paths: ['lang']
})


// Create Store
Vue.use(Vuex)


// define default state of global variables
const defaultState = () => {
    return {
        config: {
            name: '' // initialise with a default state // will be changed on start
        },
        lang: 'de',
        isReady: false, // map has initialized
        updating: false, // is something updating in map (panning, zooming, etc.)
        taxonId: null,
        taxonName: '',
        currentName: '',
        yearRange: {min: null, max: null},    // time window selected using the slider
        ecotypes: [],
        queryURL: process.env.BASE_URL + '/search?taxon=null&start=' + null + '&end=' + null,
        reliableDataBasis: 'false',
        totalObservations: 0,
        totalObservationsSelected: 0,
        total5x5boxes: 0,
        total5x5boxesSelected: 0,
        totalListEntries: 0,
        firstObservation: '/',
        lastObservation: '/',
        lowestObservation: '/',
        highestObservation: '/',
        neomyceteStatus: '/'
    }
}

function initialObservationState() {
    return {
        reliableDataBasis: 'false',
        totalObservations: 0,
        totalObservationSelected: 0,
        total5x5boxes: 0,
        totalListEntries: 0,
        firstObservation: '/',
        lastObservation: '/',
        lowestObservation: '/',
        highestObservation: '/',
        neomyceteStatus: '/'
    }
}

const store = new Vuex.Store({
    state: defaultState(),

    getters: {
        lang: state => {
            return state.lang
        },
        currentName: state => {
            return state.currentName
        },
        yearRangeMin: state => {
            return state.yearRange.min
        },
        yearRangeMax: state => {
            return state.yearRange.max
        },
        ecotypes: state => {
            return state.ecotypes
        },
        queryURL: state => {
            return state.queryURL
        },
        mapIsReady: state => {
            return state.isReady
        },
        reliableDataBasis: state => {
            return state.reliableDataBasis
        },
        totalObservations: state => {
            return state.totalObservations
        },
        totalObservationsSelected: state => {
            return state.totalObservationsSelected
        },
        total5x5boxes: state => {
            return state.total5x5boxes
        },
        total5x5boxesSelected: state => {
            return state.total5x5boxesSelected
        },
        totalListEntries: state => {
            return state.totalListEntries
        },
        firstObservation: state => {
            return state.firstObservation
        },
        lastObservation: state => {
            return state.lastObservation
        },
        lowestObservation: state => {
            return state.lowestObservation
        },
        highestObservation: state => {
            return state.highestObservation
        },
        neomyceteStatus: state => {
            return state.neomyceteStatus
        },
        isFungusApp: state => {
            return state.config.name === FUNGUS_APP_NAME
        },
        isLichenApp: state => {
            return state.config.name === LICHEN_APP_NAME
        },
        application: state => {
            return state.config.name
        }
    },
    mutations: {
        setLang(state, payload) {
            state.lang = payload
        },
        setYearMin(state, payload) {
            state.yearRange.min = payload
        },
        setYearMax(state, payload) {
            state.yearRange.max = payload
        },
        setTaxonId(state, payload) {
            state.taxonId = payload
        },
        setTaxonName(state, payload) {
            state.taxonName = payload
        },
        setCurrentName(state, payload) {
            state.currentName = payload
        },
        setTimeRange(state, payload) {
            state.yearRange = payload
        },
        setEcotypes(state, payload) {
            state.ecotypes = payload
        },
        setQueryURL(state, payload) {
            // dynamically reset url without reloading the page
            state.queryURL = payload
            window.history.pushState('updatedSearchParams', '', state.queryURL)
        },
        setUpdating(state, payload) {
            state.updating = payload
        },
        setReady(state, payload) {
            state.isReady = payload
        },
        setReliableDataBasis(state, payload) {
            state.reliableDataBasis = payload
        },
        setTotalObservations(state, payload) {
            state.totalObservations = payload
        },
        setTotalObservationsSelected(state, payload) {
            state.totalObservationsSelected = payload
        },
        setTotal5x5boxes(state, payload) {
            state.total5x5boxes = payload
        },
        setTotal5x5boxesSelected(state, payload) {
            state.total5x5boxesSelected = payload
        },
        setTotalListEntries(state, payload) {
            state.totalListEntries = payload
        },
        setFirstObservation(state, payload) {
            state.firstObservation = payload
        },
        setLastObservation(state, payload) {
            state.lastObservation = payload
        },
        setLowestObservation(state, payload) {
            state.lowestObservation = payload
        },
        setHighestObservation(state, payload) {
            state.highestObservation = payload
        },
        setNeomyceteStatus(state, payload) {
            state.neomyceteStatus = payload
        },
        resetObservationInformation(state) {
            // reset taxon-based observation statistics to default state
            const s = initialObservationState()
            Object.keys(s).forEach(key => {
                state[key] = s[key]
            })
        },
        initConfig(state, name) {
            state.config.name = name
        }

    },
    actions: {
        // Change Default behavior of Application
        init({commit}) {
            axios.get(configUrl.config).then((result) => {
                let config = result.data
                commit('initConfig',  config.name )

                if (config.name === 'FUNGUS') {
                    commit('setYearMin', SELECTED_TIME_SPAN_FUNGUS.min)
                    commit('setYearMax', SELECTED_TIME_SPAN_FUNGUS.max)
                } else if (config.name === 'LICHEN') {
                    commit('setYearMin', SELECTED_TIME_SPAN_LICHEN.min)
                    commit('setYearMax', SELECTED_TIME_SPAN_LICHEN.max)
                }

            }).catch((error) => {
                console.log(API_ERROR_MESSAGE, error)
            })

        }
    },
    plugins: [vuexSessionStorage]
})

export default store
